/**
 * branding.service.ts — Admin > Branding: the account's logos, theme colours
 * and site-logo caption (aa_accounts_branding), read and written through
 * /v5/account-branding as the corporate dashboard does. Writes need Admin
 * Access. The navigation reads the same row (navigation.service).
 *
 * Stored values, as corp writes them:
 *   - a logo is a full URL, or a bare file name under the legacy
 *     uimages/img<account>/ folder; '' = none;
 *   - a colour is 6 hex digits without '#', and "0" = none;
 *   - the caption's style is one JSON string (see v5/lib/logoSubTextFonts).
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';
import {
  parseLogoSubTextStyle,
  serialiseLogoSubTextStyle,
  type LogoSubTextStyleForm,
} from '@/v5/lib/logoSubTextFonts';

/** Where legacy logo file names live (the API's navigation uses the same host). */
const LEGACY_ASSET_BASE = 'https://app.followthatpatient.com';

export type LogoKey =
  | 'siteLogo'
  | 'siteLogoSquare'
  | 'svgLogo'
  | 'svgLogoSquare'
  | 'lightLogo'
  | 'lightLogoSquare'
  | 'darkLogo'
  | 'darkLogoSquare';

export type ColorKey = 'backgroundColor1' | 'backgroundColor2' | 'buttonColor1' | 'tabColor1';

/** Corp's logo sets, in its order (the legacy Brand Logo stays hidden, as on corp). */
export const LOGO_SETS: { key: LogoKey; squareKey: LogoKey; label: string; hint: string; svg: boolean }[] = [
  { key: 'siteLogo', squareKey: 'siteLogoSquare', label: 'Site Logo', hint: 'Logo displayed in the site navigation and header', svg: false },
  {
    key: 'svgLogo',
    squareKey: 'svgLogoSquare',
    label: 'SVG Logo',
    hint: 'Vector logo that works on any background and can inherit text colors',
    svg: true,
  },
  {
    key: 'lightLogo',
    squareKey: 'lightLogoSquare',
    label: 'Light Logo',
    hint: 'Light-colored logo for forms, receipts, and branded materials generated by Prospera with dark backgrounds',
    svg: false,
  },
  {
    key: 'darkLogo',
    squareKey: 'darkLogoSquare',
    label: 'Dark Logo',
    hint: 'Dark-colored logo for forms, receipts, and branded materials generated by Prospera with light backgrounds',
    svg: false,
  },
];

export const LOGO_KEYS: LogoKey[] = LOGO_SETS.flatMap((s) => [s.key, s.squareKey]);

export const COLOR_FIELDS: { key: ColorKey; label: string; sublabel: string }[] = [
  { key: 'backgroundColor1', label: 'Background Color 1', sublabel: 'Primary/dark background' },
  { key: 'backgroundColor2', label: 'Background Color 2', sublabel: 'Secondary background' },
  { key: 'buttonColor1', label: 'Button Color', sublabel: 'Accent/button color' },
  { key: 'tabColor1', label: 'Tab Color', sublabel: 'Tab/navigation bar' },
];

/** Legacy files shown when a slot is empty, as corp and the navigation fall back to. */
const LEGACY_LOGO_FALLBACKS: Partial<Record<LogoKey, string>> = {
  siteLogo: 'brandlogo.png',
  siteLogoSquare: 'brandlogosquare.png',
  lightLogo: 'brandlogo_generic.png',
};

export const SITE_LOGO_SUB_TEXT_MAX = 100;

export type BrandingForm = Record<LogoKey, string> &
  Record<ColorKey, string> &
  LogoSubTextStyleForm & { siteLogoSubText: string };

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

/** '' for the "0" sentinel and blanks; otherwise the hex without '#'. */
function color(v: unknown): string {
  const s = str(v);
  return !s || s === '0' ? '' : s.replace(/^#/, '');
}

export function brandingFormFrom(row: Record<string, unknown> | null): BrandingForm {
  const r = row ?? {};
  const logos = Object.fromEntries(
    LOGO_KEYS.map((k) => {
      const v = str(r[k]);
      return [k, v === '0' || v === 'null' ? '' : v];
    }),
  ) as Record<LogoKey, string>;
  return {
    ...logos,
    backgroundColor1: color(r.backgroundColor1),
    backgroundColor2: color(r.backgroundColor2),
    buttonColor1: color(r.buttonColor1),
    tabColor1: color(r.tabColor1),
    siteLogoSubText: str(r.siteLogoSubText),
    ...parseLogoSubTextStyle(r.siteLogoSubTextStyle),
  };
}

/** Everything corp saves: logos ('' when blank), caption, colours ("0" when blank), style JSON. */
export function brandingPayload(form: BrandingForm): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of LOGO_KEYS) out[k] = form[k] || '';
  out.siteLogoSubText = form.siteLogoSubText.trim();
  for (const { key } of COLOR_FIELDS) out[key] = form[key] || '0';
  out.siteLogoSubTextStyle = serialiseLogoSubTextStyle(form);
  return out;
}

/** A stored logo as a URL to show: full URLs as they are, file names under the legacy folder. */
export function resolveLogoUrl(value: string, accountId: number | null | undefined): string {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return accountId ? `${LEGACY_ASSET_BASE}/uimages/img${accountId}/${value}` : '';
}

/** The legacy file a slot falls back to while empty, or ''. */
export function legacyFallbackUrl(key: LogoKey, accountId: number | null | undefined): string {
  const file = LEGACY_LOGO_FALLBACKS[key];
  return file && accountId ? `${LEGACY_ASSET_BASE}/uimages/img${accountId}/${file}` : '';
}

export function getAccountBranding(): Promise<Record<string, unknown> | null> {
  return apiClient
    .get<ApiResponse<Record<string, unknown> | null>>('/account-branding')
    .then((r) => (r.data?.data && typeof r.data.data === 'object' ? r.data.data : null));
}

export function updateAccountBranding(payload: Record<string, string>): Promise<void> {
  return apiClient.put('/account-branding', payload).then(() => undefined);
}
